import { useState, useCallback, useEffect } from "react"
import dayjs from "dayjs"
import { useQuery } from "@apollo/client"
import { CustomState, Order, OrderCustomState, OrderHistory, OrderHistoryLogEntry } from "lib/types/generated/graphql-types"
import { useSelector } from "react-redux"
import { selectUserAttributes } from "lib/store/services/auth/selectors"
import ALL_CUSTOM_STATES from "graphql/queries/settings/customState/AllCustomStates"

export const useOrderHistoryState = (order: Order | undefined, orderHistory: OrderHistory | undefined) => {
  const [orderHistoryList, setOrderHistoryList] = useState<OrderHistoryLogEntry[]>(orderHistory?.history ?? [])
  const [customStateList, setCustomStateList] = useState<OrderCustomState[]>(order?.status?.customStates ?? [])
  const userAttributes = useSelector(selectUserAttributes)

  const { data: customStateData, error } = useQuery(ALL_CUSTOM_STATES)
  const customStates: CustomState[] = customStateData?.Order?.customStates?.states ?? []

  const getCustomState = useCallback(
    (id: string) => customStates.find((s) => s.id === id),
    [customStates]
  )

  const addCustomStateToHistory = (customStateId: string) => {
    const orderState = getCustomState(customStateId)
    const entry: OrderHistoryLogEntry = {
      actor: { name: userAttributes.email, type: "USER" },
      operation: "CUSTOM_STATE",
      operationId: customStateId,
      status: "NONE",
      timestamp: dayjs(),
      type: "ADD_CUSTOM_STATE"
    }

    if (orderState) {
      const state = {
        id: orderState.id,
        type: orderState.type,
        operations: orderState.operations
      }
      setCustomStateList(prev => [...prev, state])
    }
    setOrderHistoryList(prev => [...prev, entry])
  }

  useEffect(() => {
    setOrderHistoryList(orderHistory?.history ?? [])
    setCustomStateList(order?.status?.customStates ?? [])
  }, [orderHistory, order?.status])

  return {
    orderHistoryList,
    setOrderHistoryList,
    customStateList,
    setCustomStateList,
    customStates,
    getCustomState,
    addCustomStateToHistory,
    error
  }
}
